<template lang="html">
    <div
        class="allocations-table"
        :class="{'pdf-layout': pdfLayout}"
    >
        <b-field
            v-if="!pdfLayout"
            grouped
            group-multiline
        >
            <b-dropdown
                v-model="filterSource"
                multiple
                role="list"
            >
                <b-button
                    slot="trigger"
                    :type="{'is-info': filterSource.length}"
                    outlined
                >
                    <span>Source</span>
                </b-button>
                <b-dropdown-item
                    v-for="(source, index) in sources"
                    :key="index"
                    :value="source.value"
                    role="listitem"
                >
                    <span>{{ source.title }}</span>
                </b-dropdown-item>
            </b-dropdown>

            <b-dropdown
                v-model="filterTerm"
                multiple
                role="list"
            >
                <b-button
                    slot="trigger"
                    :type="{'is-info': filterTerm.length}"
                    outlined
                >
                    <span>Term</span>
                </b-button>
                <b-dropdown-item
                    v-for="(term, index) in terms"
                    :key="index"
                    :value="term.value"
                    role="listitem"
                >
                    <span>{{ term.title }}</span>
                </b-dropdown-item>
            </b-dropdown>

            <b-dropdown
                v-model="filterPurpose"
                multiple
                role="list"
            >
                <b-button
                    slot="trigger"
                    :type="{'is-info': filterPurpose.length}"
                    outlined
                >
                    <span>Purpose</span>
                </b-button>
                <b-dropdown-item
                    v-for="(purpose, index) in industries"
                    :key="index"
                    :value="purpose"
                    role="listitem"
                >
                    <span>{{ purpose }}</span>
                </b-dropdown-item>
            </b-dropdown>

            <b-input
                v-model="filterSearch"
                placeholder="Text Search"
                icon="magnify"
                class="filter-text-search"
            />

            <b-button
                type="is-info"
                @click="resetFilters"
            >
                Reset Filters
            </b-button>
        </b-field>
        <b-table
            ref="AllocationsTable"
            :data="filteredData"
            detailed
            :show-detail-icon="false"
            :paginated="showPagination"
            pagination-size="is-small"
            :default-sort-direction="sortDirection"
            :default-sort="sortedColumn"
            sort-icon-size="is-small"
            :per-page="rowsPerPage"
            pagination-position="bottom"
            :striped="pdfLayout"
            :is-fullwidth="true"
            :narrowed="true"
            :row-class="(row, index) => row.highlight ? `${row.type} highlight` : row.type"
        >
            <template slot-scope="props">
                <b-table-column
                    field="licensee"
                    label="Licence"
                    sortable
                    :class="{'web-layout': !pdfLayout}"
                >
                    <div
                        class="licence"
                        :class="{'web-layout': !pdfLayout}"
                    >
                        <div class="licensee">
                            {{ props.row.licensee }}
                        </div>
                        <div class="licence-type is-size-7 has-text-grey">
                            {{ props.row.purpose }} from {{ props.row.stream_name }}
                            <span v-if="props.row.sourcetype">({{ props.row.sourcetype }})</span>
                        </div>
                    </div>
                    <!-- Toggle for expandable section -->
                    <b-button
                        v-if="(props.row.hydraulic_connectivity || arrayHasValues(props.row.documentation))
                            && !pdfLayout"
                        type="is-text"
                        class="expand-row-link"
                        @click="$refs.AllocationsTable.toggleDetails(props.row)"
                    >
                        <b-icon
                            pack="mdi"
                            icon="chevron-down"
                            type="is-secondary"
                        />
                        <span>Licence Details</span>
                    </b-button>
                </b-table-column>
                <b-table-column
                    field="licence_no"
                    label="Number"
                    sortable
                >
                    <div :class="{'licence-number-wrapper': props.row.well_tag_number, 'web-layout': !pdfLayout}">
                        <div
                            class="licence-number"
                            :class="{'single-licence-number': !props.row.well_tag_number, 'web-layout': !pdfLayout}"
                        >
                            {{ props.row.licence_no }}
                        </div>
                        <div
                            v-if="props.row.well_tag_number"
                            class="file-number"
                        >
                            File # {{ props.row.file_no }}
                        </div>
                    </div>
                </b-table-column>
                <b-table-column
                    field="pod"
                    label="POD"
                    sortable
                >
                    <div :class="{'pod-wrapper': props.row.well_tag_number, 'web-layout': !pdfLayout}">
                        <div
                            class="licence-number"
                            :class="{'single-pod-number': !props.row.well_tag_number, 'web-layout': !pdfLayout}"
                        >
                            {{ props.row.pod }}
                        </div>
                        <div
                            v-if="props.row.well_tag_number"
                            class="wtn-number"
                        >
                            WTN: {{ props.row.well_tag_number }}
                        </div>
                    </div>
                </b-table-column>
                <b-table-column
                    field="priority_date"
                    label="Date"
                    sortable
                >
                    <div
                        class="date-wrapper"
                        :class="{'web-layout': !pdfLayout}"
                    >
                        <div
                            v-if="props.row.priority_date"
                            class="priority-date"
                        >
                            Priority: {{ formatDateObject(props.row.priority_date) }}
                        </div>
                        <div
                            v-if="props.row.start_date"
                            class="start-date"
                        >
                            Start: {{ formatDateObject(props.row.start_date) }}
                        </div>
                        <div
                            v-if="props.row.expiry_date"
                            class="expiry-date"
                        >
                            Exp.: {{ formatDateObject(props.row.expiry_date) }}
                        </div>
                        <div
                            v-if="props.row.lic_status_date"
                            class="status-date"
                        >
                            Status: {{ formatDateObject(props.row.lic_status_date) }}
                        </div>
                    </div>
                </b-table-column>
                <b-table-column
                    field="qty_display"
                    label="Quantity"
                    sortable
                >
                    <div
                        class="quantity"
                        :class="{'web-layout': !pdfLayout}"
                    >
                        {{ props.row.qty_display }}
                    </div>
                </b-table-column>
                <b-table-column
                    field="flag_desc"
                    label="Flag"
                    sortable
                >
                    <template
                        slot="header"
                        slot-scope="{ column }"
                    >
                        {{ column.label }}<sup>
                            <footnote-tooltip
                                :footnote-number="12"
                            />
                        </sup>
                    </template>
                    <div
                        class="flag has-text-centered"
                        :class="{'web-layout': !pdfLayout}"
                    >
                        {{ props.row.flag_desc ? props.row.flag_desc.toString() : '' }}
                    </div>
                </b-table-column>
                <b-table-column
                    v-if="!pdfLayout"
                    field="lic_type"
                    label="Type"
                    sortable
                >
                    <div
                        class="type"
                        :class="{'web-layout': !pdfLayout}"
                    >
                        <b-tooltip
                            :label="props.row.lic_type_tt"
                            class="type-tooltip"
                            type="is-light"
                        >
                            <span :class="['tag', `is-${props.row.lic_type}`]">
                                {{ props.row.lic_type }}
                            </span>
                        </b-tooltip>
                    </div>
                </b-table-column>
                <b-table-column
                    v-if="!pdfLayout"
                    field="lic_status"
                    label="Status"
                    sortable
                    centered
                >
                    <div
                        class="status"
                        :class="{'web-layout': !pdfLayout}"
                    >
                        <b-tooltip
                            v-if="props.row.lic_status === 'CURRENT'"
                            label="Current"
                            class="status-tooltip"
                            type="is-light"
                        >
                            <b-icon
                                class="status-icon"
                                pack="mdi"
                                icon="check-circle"
                                type="is-success"
                            />
                        </b-tooltip>
                        <b-tooltip
                            v-else
                            label="Active Application"
                            class="status-tooltip"
                            type="is-light"
                        >
                            <b-icon
                                class="status-icon"
                                pack="mdi"
                                icon="minus-circle"
                                type="is-warning"
                            />
                        </b-tooltip>
                    </div>
                </b-table-column>
            </template>

            <!-- Empty results section -->
            <template slot="empty">
                <section class="section">
                    <div class="content has-text-grey has-text-centered">
                        <p>
                            <b-icon
                                icon="emoticon-sad"
                                size="is-large"
                            />
                        </p>
                        <p>No results</p>
                        <b-button
                            type="is-info"
                            @click="resetFilters"
                        >
                            Reset Filters
                        </b-button>
                    </div>
                </section>
            </template>

            <!-- Expandable row section -->
            <template
                v-if="(props.row.hydraulic_connectivity || arrayHasValues(props.row.documentation)) && !pdfLayout"
                slot="detail"
                slot-scope="props"
            >
                <div>
                    <div v-if="props.row.hydraulic_connectivity">
                        Hydraulic Connectivity: {{ props.row.hydraulic_connectivity }}
                    </div>
                    <br v-if="props.row.hydraulic_connectivity && arrayHasValues(props.row.documentation)">
                    <div v-if="arrayHasValues(props.row.documentation)">
                        Documents:
                        <div
                            v-for="(item, index) in props.row.documentation"
                            :key="index"
                        >
                            {{ item.fileName }}:
                            <a
                                :href="item.linkUrl"
                                target="_blank"
                            >{{ item.linkUrl }}</a>
                        </div>
                    </div>
                </div>
            </template>
        </b-table>
        <b-select
            v-if="!pdfLayout"
            v-model="rowsPerPage"
            size="is-small"
            class="select-per-page"
        >
            <option
                v-for="option in rowsPerPageOptions"
                :key="option"
                :value="option"
            >
                {{ option }} per page
            </option>
        </b-select>
    </div>
</template>

<script>
import FootnoteTooltip from '@foundryspatial/footnote-tooltip';
import moment from 'moment';

export default {
    components: {
        FootnoteTooltip,
    },

    props: {
        licences: {
            type: Array,
            required: true,
        },

        perPage: {
            type: [Number, String],
            required: false,
            default: 10,
        },

        // This prop changes the layout of the table to fit with what the pdf expects.
        // True: hides licence status and type columns; removes document dropdown section
        pdfLayout: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    data: () => ({
        rowsPerPage: 10,
        rowsPerPageOptions: [15, 25, 50, 100],
        filterSource: [],
        filterPurpose: [],
        filterTerm: [],
        filterSearch: '',
        sources: [
            {
                value: 'GW',
                title: 'Ground Water',
            },
            {
                value: 'SW',
                title: 'Surface Water',
            },
        ],

        terms: [
            {
                value: 'lic',
                title: 'Long',
            },
            {
                value: 'stu',
                title: 'Short',
            },
            {
                value: 'app',
                title: 'Application',
            },
        ],

        industries: [
            'Agriculture',
            'Commercial',
            'Domestic',
            'Municipal',
            'Power',
            'Oil & Gas',
            'Other',
        ],
    }),

    computed: {
        // show pagination buttons if there is more than one page of values
        showPagination() {
            return (this.filteredData.length > this.rowsPerPage) && !this.pdfLayout;
        },

        // change sort column depending on if pdfLayout is true or false
        sortedColumn() {
            return this.pdfLayout ? 'total_vol' : 'priority_date';
        },

        // change sort direction for pdf layout
        sortDirection() {
            return this.pdfLayout ? 'desc' : 'asc';
        },

        // Get the filtered licences according to the filters set on the table
        filteredData() {
            let filtered = this.licences;
            if (this.filterSource.length) {
                filtered = filtered.filter((row) => this.filterSource.includes(row.water_allocation_type));
            }
            if (this.filterTerm.length) {
                // filter based on end part of 'lic_type' (lic, stu, app)
                filtered = filtered.filter((row) => this.filterTerm.includes(row.lic_type.substring(3)));
            }
            if (this.filterPurpose.length) {
                filtered = filtered.filter((row) => this.filterPurpose.includes(row.purpose_groups));
            }
            // search through the sourceDetail and source to filter the results with a free text search
            if (this.filterSearch !== '') {
                filtered = filtered.filter((row) => this.filterTextSearch(this.filterSearch, row));
            }
            return filtered;
        },
    },

    mounted() {
        // set our data value to the given value
        this.rowsPerPage = this.perPage;
        // add the given value to our dropdown options, ensuring they're in the correct numerical order
        this.rowsPerPageOptions.push(Number(this.perPage));
        this.rowsPerPageOptions.sort((a, b) => a - b);
    },

    methods: {
        /**
         * Format date into more readable display
         *
         * @param {string} dateString - date to format
         * @returns {string} - formatted date
         */
        formatDateObject(dateString) {
            return moment(dateString, 'YYYY-MM-DD').format('MMM DD, YYYY');
        },

        /**
         * Check if array contains any non null values
         *
         * @param {Array} array - array to check
         * @returns {boolean} - whether or not array has values
         */
        arrayHasValues(array) {
            if (!array || array.some((d) => d === null)) {
                return false;
            }
            return true;
        },

        /**
         * Searches each row for matches of searched text in specified columns
         *
         * @param {string} searchText - text provided by user
         * @param {object} row - row object from filtered results set
         * @returns {boolean} - true if the row matches with the search text
         */
        filterTextSearch(searchText, row) {
            const findIn = `${row.licensee} ${row.stream_name} ${row.sourcetype}
                ${row.well_tag_number} ${row.pod} ${row.file_no} ${row.licence_no} ${row.lic_type}
                ${row.lic_type_tt} ${row.lic_status} ${row.purpose}`;
            if (findIn.toUpperCase().indexOf(searchText.toUpperCase()) !== -1) {
                return true;
            }
            return false;
        },

        /**
         * Resets filters to default
         */
        resetFilters() {
            this.filterSource = [];
            this.filterPurpose = [];
            this.filterTerm = [];
            this.filterSearch = '';
        },
    },
};
</script>

<style lang="scss">
div.allocations-table {
    $color-highlight-licence: rgb(255, 102, 0);
    // licence row height
    $row-height: 61px;

    display: flex;
    flex-direction: column;
    padding: 0 8px;
    font-size: 13px;
    color: rgb(68, 67, 67);

    &.pdf-layout {
        font-size: 10px;
    }

    .filter-text-search {
        margin-left: 0.5em;
    }

    // compensate margin so paginator isn't too far from table
    .table-wrapper {
        margin-bottom: 1.5rem;
    }

    .level {
        margin-top: -1.5rem;
    }

    // fix styling issues on pagination selector
    ul.pagination-list {
        list-style: none;
        margin-top: 0;

        li {
            margin-top: 0;
        }
    }

    // Set table header styles
    thead {
        line-height: 1.4;
        font-size: 16px;

        // styling to center Flag header over column
        th {
            // get the correct header
            &:nth-child(6) {
                // the left padding below makes the whole elem wider, and Flag label ends up too far right, so
                // counteracting it here
                padding-right: 5px;

                .th-wrap {
                    // push the label into the center
                    padding-left: 15px;

                    span {
                        // remove the sort icon when it's invisible
                        &.is-invisible {
                            display: none;
                        }

                        // prevent Flag label from wrapping and line it up vertically with other headers
                        &:not(.icon) {
                            white-space: nowrap;
                            margin-top: -3px;
                        }
                    }
                }
            }
        }
    }

    // Set table body styles
    tbody {
        height: $row-height;
        text-overflow: ellipsis;
        white-space: nowrap;

        tr {
            &.highlight {
                background: lightgrey;

                .licensee {
                    color: $color-highlight-licence;
                }
            }

            td {
                padding: 0 8px;
                line-height: 1.4;

                &.web-layout {
                    height: $row-height;
                }

                .quantity {
                    white-space: normal;
                }

                .expand-row-link {
                    color: $secondary;
                    text-decoration: none;

                    // the linter is complaining because of the span rule that's under th above. To fix it we either
                    // need to add several more layers of specificity or move this rule above the th rule, both of
                    // which will reduce code readability. And so we're choosing to ignore the rule here.
                    // stylelint-disable-next-line no-descending-specificity
                    span {
                        display: flex;
                    }
                }

                .single-licence-number,
                .single-pod-number,
                .quantity,
                .pump-rate,
                .type,
                .status,
                .flag {
                    &.web-layout {
                        padding-top: 11px;
                    }

                    .type-tooltip,
                    .status-tooltip {
                        line-height: 1;
                    }

                    .icon.status-icon {
                        i {
                            margin-bottom: -3em;
                        }
                    }
                }

                .licence,
                .date-wrapper,
                .pod-wrapper,
                .licence-number-wrapper {
                    line-height: 1.4;

                    &.web-layout {
                        padding-top: 11px;
                        padding-bottom: 11px;
                    }

                    .licensee,
                    .licence-type {
                        text-overflow: ellipsis;
                        white-space: initial;
                        overflow: hidden;
                    }
                }

                .priority-date {
                    &.height-fix {
                        line-height: 15px;
                        padding: 15px 0;
                    }
                }
            }

            &:last-child {
                td {
                    border-bottom-width: 1px;
                }
            }
        }
    }

    .level:not(.top) {
        padding-top: 1.5em;
        padding-bottom: 1em;
    }

    .select-per-page {
        align-self: flex-end;
    }
}
</style>
